import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  TextField,
  Grid,
  CircularProgress,
  Alert,
  Switch,
  FormControlLabel
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import apiClient from '../../services/api';
import ToastService from '../../core/notifications/ToastService';

interface Slot {
  id: number;
  date: string;
  startTime: string;
  endTime: string;
  totalCapacity: number;
  priceCoins: number;
  priceCash: number;
  isActive: boolean;
  description?: string;
}

export const EditSlotPage: React.FC = () => {
  const { gymId, slotId } = useParams<{ gymId: string; slotId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [slot, setSlot] = useState<Slot | null>(null);
  const [formData, setFormData] = useState({
    capacity: '',
    price_coins: '',
    price_cash: '',
    is_active: true,
    description: ''
  });

  const fetchSlot = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get<any>(`/slots/gyms/${gymId}/slots/${slotId}`);
      const slotData = (response as any).data;
      setSlot(slotData);
      setFormData({
        capacity: slotData.totalCapacity.toString(),
        price_coins: slotData.priceCoins.toString(),
        price_cash: slotData.priceCash.toString(),
        is_active: slotData.isActive,
        description: slotData.description || ''
      });
    } catch (err: any) {
      const message = err.response?.data?.message || 'Failed to fetch slot';
      setError(message);
      ToastService.error(message);
    } finally {
      setLoading(false);
    }
  }, [gymId, slotId]);

  useEffect(() => {
    if (gymId && slotId) {
      fetchSlot();
    }
  }, [gymId, slotId, fetchSlot]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    setError(null);
  };

  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      is_active: e.target.checked
    }));
  };

  const validateForm = (): boolean => {
    if (!formData.capacity || !formData.price_coins || !formData.price_cash) {
      setError('All required fields must be filled');
      return false;
    }

    if (parseInt(formData.capacity) < 1) {
      setError('Capacity must be at least 1');
      return false;
    }

    if (parseInt(formData.price_coins) < 0 || parseInt(formData.price_cash) < 0) {
      setError('Prices cannot be negative');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      setActionLoading(true);
      setError(null);

      await apiClient.put(`/slots/gyms/${gymId}/slots/${slotId}`, {
        capacity: parseInt(formData.capacity),
        price_coins: parseInt(formData.price_coins),
        price_cash: parseInt(formData.price_cash),
        is_active: formData.is_active,
        description: formData.description || null
      });

      ToastService.success('Slot updated successfully');
      navigate(`/pricing/gyms/${gymId}/slots`);
    } catch (err: any) {
      const message = err.response?.data?.message || 'Failed to update slot';
      setError(message);
      ToastService.error(message);
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!slot) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">Slot not found</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/pricing/gyms/${gymId}/slots`)}
          sx={{ mt: 2 }}
        >
          Back to Slots
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/pricing/gyms/${gymId}/slots`)}
        >
          Back
        </Button>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
            Edit Slot
          </Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>
            {slot.date} • {slot.startTime} - {slot.endTime}
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Form */}
      <Card>
        <CardContent>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {/* Slot Info (Read-only) */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2, color: '#666' }}>
                  Slot Information (Read-only)
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Date"
                      value={slot.date}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      label="Start Time"
                      value={slot.startTime}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      label="End Time"
                      value={slot.endTime}
                      disabled
                    />
                  </Grid>
                </Grid>
              </Grid>

              {/* Editable Fields */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2, color: '#666' }}>
                  Editable Information
                </Typography>
              </Grid>

              {/* Capacity */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Capacity"
                  name="capacity"
                  value={formData.capacity}
                  onChange={handleChange}
                  inputProps={{ min: 1 }}
                  required
                />
              </Grid>

              {/* Status */}
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.is_active}
                      onChange={handleSwitchChange}
                      name="is_active"
                    />
                  }
                  label={formData.is_active ? 'Active' : 'Inactive'}
                  sx={{ mt: 1 }}
                />
              </Grid>

              {/* Pricing */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Price (Coins)"
                  name="price_coins"
                  value={formData.price_coins}
                  onChange={handleChange}
                  inputProps={{ min: 0 }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Price (Cash)"
                  name="price_cash"
                  value={formData.price_cash}
                  onChange={handleChange}
                  inputProps={{ min: 0 }}
                  required
                />
              </Grid>

              {/* Description */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Description (Optional)"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                />
              </Grid>

              {/* Actions */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  <Button
                    onClick={() => navigate(`/pricing/gyms/${gymId}/slots`)}
                    disabled={actionLoading}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={actionLoading}
                    sx={{ backgroundColor: '#14B8A6' }}
                  >
                    {actionLoading ? <CircularProgress size={24} /> : 'Save Changes'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>
    </Container>
  );
};

export default EditSlotPage;
